import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Check, PenLine } from 'lucide-react';
import { useCompleteOnboardingMutation, useMeQuery, useUpdateProfileMutation } from '../profiles/api';
import { AvatarEditor } from '../profiles/AvatarEditor';
import { SignatureCard } from '../personal/Signature';
import { apiErrorMessage } from '../letters/ui';
import { Dots, HowItWorksCards } from './HowItWorks';
import { InviteChoices } from './InviteChoices';
import { ShareKit } from './ShareKit';
import { toiypAddress } from './share';
import { firstRunSteps, howCards } from './steps';
import { track } from './track';

/**
 * The first-run tour (onboarding plan, slice 1): shown once per account, right
 * after the handle is claimed. Every step can be set aside with "Later", which
 * ends the tour for good — it is a welcome, not a gate.
 *
 * Someone who came through a friend's invite link sees a shorter tour and then
 * lands on their blank letter to that friend, exactly as before.
 */
export function FirstRun({
  viaInviteLink,
  onStartedConversation,
}: {
  viaInviteLink: boolean;
  /** "Already on toiyp?" began a correspondence: the app opens it once the tour ends. */
  onStartedConversation: (relationshipId: string) => void;
}) {
  const { data: me } = useMeQuery();
  const [complete, { isLoading: finishing }] = useCompleteOnboardingMutation();
  const steps = firstRunSteps(viaInviteLink);
  const cards = howCards(viaInviteLink);
  const [stepIndex, setStepIndex] = useState(0);
  const [card, setCard] = useState(0);
  // A conversation started from "Already on toiyp?" — opened when the tour ends.
  const [startedId, setStartedId] = useState<string | null>(null);
  const step = steps[stepIndex]!;
  const stepName = step === 'how' ? `how_${cards[card]}` : step;

  useEffect(() => {
    track({ event: 'onboarding_step_viewed', step: stepName });
  }, [stepName]);

  const finish = useCallback(async () => {
    if (startedId) onStartedConversation(startedId);
    try {
      await complete().unwrap();
    } catch {
      // The tour is a courtesy: never trap someone in it because the network
      // blinked. It will simply be offered again next time.
    }
  }, [complete, startedId, onStartedConversation]);

  const later = useCallback(() => {
    track({ event: 'onboarding_step_skipped', step: stepName });
    void finish();
  }, [finish, stepName]);

  const next = useCallback(() => setStepIndex((i) => Math.min(i + 1, steps.length - 1)), [steps.length]);

  if (!me?.handle) return null;

  return (
    <main className="first-run">
      <header className="first-run__bar">
        <Dots count={steps.length} at={stepIndex} label="Step" />
        {step !== 'done' && (
          <button type="button" className="first-run__later" onClick={later} disabled={finishing}>
            Later
          </button>
        )}
      </header>

      {step === 'how' && (
        <div className="first-run__step first-run__step--how">
          <p className="first-run__kicker">How toiyp works</p>
          <HowItWorksCards cards={cards} index={card} onIndexChange={setCard} handle={me.handle} />
          <div className="first-run__foot">
            <button
              type="button"
              className="btn btn--primary btn--block"
              onClick={() => (card < cards.length - 1 ? setCard(card + 1) : next())}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {step === 'face' && <FaceStep onNext={next} />}

      {step === 'invite' && (
        <InviteStep
          handle={me.handle}
          onStarted={(id) => {
            setStartedId(id);
            next();
          }}
          onNext={next}
        />
      )}

      {step === 'share' && <ShareStep onNext={next} />}

      {step === 'done' && (
        <DoneStep
          handle={me.handle}
          viaInviteLink={viaInviteLink}
          startedWith={startedId !== null}
          busy={finishing}
          onFinish={() => void finish()}
        />
      )}
    </main>
  );
}

function StepFoot(props: { label: string; busy?: boolean; onClick: () => void }) {
  return (
    <div className="first-run__foot">
      <button type="button" className="btn btn--primary btn--block" disabled={props.busy} onClick={props.onClick}>
        {props.busy ? '…' : props.label}
      </button>
    </div>
  );
}

/* ── Put a face to your letters ─────────────────────────────────────────── */

function FaceStep({ onNext }: { onNext: () => void }) {
  const { data: me } = useMeQuery();
  const [save, { isLoading: saving }] = useUpdateProfileMutation();
  const [displayName, setDisplayName] = useState(me?.displayName ?? '');
  const [bio, setBio] = useState(me?.bio ?? '');
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onContinue = useCallback(async () => {
    if (!me) return;
    setError(null);
    const name = displayName.trim();
    const line = bio.trim();
    const patch: { displayName?: string; bio?: string } = {};
    if (name && name !== me.displayName) patch.displayName = name;
    if (line !== (me.bio ?? '')) patch.bio = line;
    try {
      if (Object.keys(patch).length > 0) await save(patch).unwrap();
      if (patch.bio) track({ event: 'bio_set' });
      onNext();
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not save that just now'));
    }
  }, [me, displayName, bio, save, onNext]);

  if (!me) return null;

  return (
    <div className="first-run__step">
      <h1 className="first-run__title">Put a face to your letters</h1>
      <p className="first-run__lead">
        A photo works best: people write more warmly to a face they recognise.
      </p>

      <AvatarEditor displayName={me.displayName} avatar={me.avatar} photoFirst />

      <div className="field">
        <label className="field__label" htmlFor="first-run-name">Your name</label>
        <input
          id="first-run-name"
          className="field__input"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={80}
          autoComplete="name"
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="first-run-bio">A line about you</label>
        <input
          id="first-run-bio"
          className="field__input"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={280}
          placeholder="Tea first, letters second. Hopeless with houseplants."
        />
      </div>

      {signing ? (
        <SignatureCard signature={me.signature} handle={me.handle} />
      ) : (
        <button type="button" className="linkish first-run__sign" onClick={() => setSigning(true)}>
          <PenLine size={13} aria-hidden="true" /> Sign your letters too (optional)
        </button>
      )}

      {error && (
        <p className="form-error" role="alert">
          <AlertCircle size={14} aria-hidden="true" /> {error}
        </p>
      )}

      <StepFoot label="Next" busy={saving} onClick={() => void onContinue()} />
    </div>
  );
}

/* ── Who'd you like to hear from? ───────────────────────────────────────── */

function InviteStep({
  handle,
  onStarted,
  onNext,
}: {
  handle: string;
  onStarted: (relationshipId: string) => void;
  onNext: () => void;
}) {
  return (
    <div className="first-run__step">
      <h1 className="first-run__title">Who&apos;d you like to hear from?</h1>
      <p className="first-run__lead">
        Send your toiyp to someone you&apos;d love a letter from. They&apos;ll find a blank page
        addressed to <span className="toiyp-address">{toiypAddress(handle)}</span>.
      </p>

      <InviteChoices handle={handle} onStarted={onStarted} />

      <StepFoot label="Next" onClick={onNext} />
    </div>
  );
}

/* ── Put your toiyp everywhere ─────────────────────────────────────────── */

function ShareStep({ onNext }: { onNext: () => void }) {
  return (
    <div className="first-run__step">
      <h1 className="first-run__title">Put your toiyp everywhere</h1>
      <p className="first-run__lead">
        Wherever people find you, let them find your letterbox too. Pick one, or come back to it
        later from the ⋯ menu.
      </p>
      <ShareKit from="tour" />
      <StepFoot label="Next" onClick={onNext} />
    </div>
  );
}

/* ── You're all set ─────────────────────────────────────────────────────── */

function DoneStep({
  handle,
  viaInviteLink,
  startedWith,
  busy,
  onFinish,
}: {
  handle: string;
  viaInviteLink: boolean;
  startedWith: boolean;
  busy: boolean;
  onFinish: () => void;
}) {
  return (
    <div className="first-run__step first-run__step--done">
      <span className="first-run__done-mark" aria-hidden="true">
        <Check size={28} />
      </span>
      <h1 className="first-run__title">You&apos;re all set</h1>
      <p className="first-run__lead">
        You&apos;re <span className="toiyp-address">{toiypAddress(handle)}</span>.{' '}
        {viaInviteLink || startedWith
          ? 'Your blank page is waiting. Take your time with it.'
          : 'Letters that arrive will be waiting here. Take your time with them.'}
      </p>
      <StepFoot
        label={viaInviteLink || startedWith ? 'Start writing' : 'Open my letters'}
        busy={busy}
        onClick={onFinish}
      />
    </div>
  );
}

